import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Input, 
  Switch, 
  Select, 
  Button, 
  Upload, 
  Modal, 
  Table, 
  Tag,
  Typography, 
  Space,
  message,
  Divider,
  Avatar,
  Popconfirm,
  Spin,
  Alert,
} from 'antd'
import {
  SettingOutlined,
  GlobalOutlined,
  BellOutlined,
  SafetyOutlined,
  ToolOutlined,
  InfoCircleOutlined,
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import type { UploadProps } from 'antd'
import { settingsService, type SystemSettings, type OperationArea } from '@services/settingsService'
import { authService } from '@services/authService'
import { useNavigate } from 'react-router-dom'
import { UserAddOutlined } from '@ant-design/icons'
import { useAppSelector } from '@store'
import { uploadImage } from '@utils/compressImage'
import { apiService } from '@services/apiService'
import AdminAccountsPanel from '@components/AdminAccountsPanel/AdminAccountsPanel'

const { Title, Text } = Typography
const { Option } = Select

const SystemSettings: React.FC = () => {
  const navigate = useNavigate()
  const authRole = useAppSelector((s) => s.auth.admin?.role)
  /** 仅母账号（超级管理员）可授权、调整、删除其他管理员 */
  const canManageAdmins = authRole === 'SUPER_ADMIN'
  const isSuperAdmin = authRole === 'SUPER_ADMIN'

  const [form] = Form.useForm()
  const [pwdForm] = Form.useForm()
  const [loading, setLoading] = useState(true)
  const [pwdModalOpen, setPwdModalOpen] = useState(false)
  
  // 系统设置数据
  const [settings, setSettings] = useState<SystemSettings | null>(null)
  
  // 运营范围管理
  const [operationAreas, setOperationAreas] = useState<OperationArea[]>([])
  const [showOperationAreasModal, setShowOperationAreasModal] = useState(false)
  const [newArea, setNewArea] = useState({ province: '', city: '' })
  const [operationAreasLoading, setOperationAreasLoading] = useState(false)
  
  // 省份和城市数据
  const provinces = [
    '北京市', '天津市', '河北省', '山西省', '内蒙古自治区',
    '辽宁省', '吉林省', '黑龙江省', '上海市', '江苏省',
    '浙江省', '安徽省', '福建省', '江西省', '山东省',
    '河南省', '湖北省', '湖南省', '广东省', '广西壮族自治区',
    '海南省', '重庆市', '四川省', '贵州省', '云南省',
    '西藏自治区', '陕西省', '甘肃省', '青海省', '宁夏回族自治区',
    '新疆维吾尔自治区'
  ]
  
  const cities: Record<string, string[]> = {
    '北京市': ['北京市'],
    '天津市': ['天津市'],
    '河北省': ['石家庄市', '唐山市', '秦皇岛市', '邯郸市', '邢台市', '保定市', '张家口市', '承德市', '沧州市', '廊坊市', '衡水市'],
    '山西省': ['太原市', '大同市', '阳泉市', '长治市', '晋城市', '朔州市', '晋中市', '运城市', '忻州市', '临汾市', '吕梁市'],
    '内蒙古自治区': ['呼和浩特市', '包头市', '乌海市', '赤峰市', '通辽市', '鄂尔多斯市', '呼伦贝尔市', '巴彦淖尔市', '乌兰察布市', '兴安盟', '锡林郭勒盟', '阿拉善盟'],
    '辽宁省': ['沈阳市', '大连市', '鞍山市', '抚顺市', '本溪市', '丹东市', '锦州市', '营口市', '阜新市', '辽阳市', '盘锦市', '铁岭市', '朝阳市', '葫芦岛市'],
    '吉林省': ['长春市', '吉林市', '四平市', '辽源市', '通化市', '白山市', '松原市', '白城市', '延边朝鲜族自治州'],
    '黑龙江省': ['哈尔滨市', '齐齐哈尔市', '鸡西市', '鹤岗市', '双鸭山市', '大庆市', '伊春市', '佳木斯市', '七台河市', '牡丹江市', '黑河市', '绥化市', '大兴安岭地区'],
    '上海市': ['上海市'],
    '江苏省': ['南京市', '无锡市', '徐州市', '常州市', '苏州市', '南通市', '连云港市', '淮安市', '盐城市', '扬州市', '镇江市', '泰州市', '宿迁市'],
    '浙江省': ['杭州市', '宁波市', '温州市', '嘉兴市', '湖州市', '绍兴市', '金华市', '衢州市', '舟山市', '台州市', '丽水市'],
    '安徽省': ['合肥市', '芜湖市', '蚌埠市', '淮南市', '马鞍山市', '淮北市', '铜陵市', '安庆市', '黄山市', '滁州市', '阜阳市', '宿州市', '六安市', '亳州市', '池州市', '宣城市'],
    '福建省': ['福州市', '厦门市', '莆田市', '三明市', '泉州市', '漳州市', '南平市', '龙岩市', '宁德市'],
    '江西省': ['南昌市', '景德镇市', '萍乡市', '九江市', '新余市', '鹰潭市', '赣州市', '吉安市', '宜春市', '抚州市', '上饶市'],
    '山东省': ['济南市', '青岛市', '淄博市', '枣庄市', '东营市', '烟台市', '潍坊市', '济宁市', '泰安市', '威海市', '日照市', '临沂市', '德州市', '聊城市', '滨州市', '菏泽市'],
    '河南省': ['郑州市', '开封市', '洛阳市', '平顶山市', '安阳市', '鹤壁市', '新乡市', '焦作市', '濮阳市', '许昌市', '漯河市', '三门峡市', '南阳市', '商丘市', '信阳市', '周口市', '驻马店市', '济源市'],
    '湖北省': ['武汉市', '黄石市', '十堰市', '宜昌市', '襄阳市', '鄂州市', '荆门市', '孝感市', '荆州市', '黄冈市', '咸宁市', '随州市', '恩施土家族苗族自治州', '仙桃市', '潜江市', '天门市', '神农架林区'],
    '湖南省': ['长沙市', '株洲市', '湘潭市', '衡阳市', '邵阳市', '岳阳市', '常德市', '张家界市', '益阳市', '郴州市', '永州市', '怀化市', '娄底市', '湘西土家族苗族自治州'],
    '广东省': ['广州市', '韶关市', '深圳市', '珠海市', '汕头市', '佛山市', '江门市', '湛江市', '茂名市', '肇庆市', '惠州市', '梅州市', '汕尾市', '河源市', '阳江市', '清远市', '东莞市', '中山市', '潮州市', '揭阳市', '云浮市'],
    '广西壮族自治区': ['南宁市', '柳州市', '桂林市', '梧州市', '北海市', '防城港市', '钦州市', '贵港市', '玉林市', '百色市', '贺州市', '河池市', '来宾市', '崇左市'],
    '海南省': ['海口市', '三亚市', '三沙市', '儋州市', '五指山市', '琼海市', '文昌市', '万宁市', '东方市', '定安县', '屯昌县', '澄迈县', '临高县', '白沙黎族自治县', '昌江黎族自治县', '乐东黎族自治县', '陵水黎族自治县', '保亭黎族苗族自治县', '琼中黎族苗族自治县'],
    '重庆市': ['重庆市'],
    '四川省': ['成都市', '自贡市', '攀枝花市', '泸州市', '德阳市', '绵阳市', '广元市', '遂宁市', '内江市', '乐山市', '南充市', '眉山市', '宜宾市', '广安市', '达州市', '雅安市', '巴中市', '资阳市', '阿坝藏族羌族自治州', '甘孜藏族自治州', '凉山彝族自治州'],
    '贵州省': ['贵阳市', '六盘水市', '遵义市', '安顺市', '毕节市', '铜仁市', '黔西南布依族苗族自治州', '黔东南苗族侗族自治州', '黔南布依族苗族自治州'],
    '云南省': ['昆明市', '曲靖市', '玉溪市', '保山市', '昭通市', '丽江市', '普洱市', '临沧市', '楚雄彝族自治州', '红河哈尼族彝族自治州', '文山壮族苗族自治州', '西双版纳傣族自治州', '大理白族自治州', '德宏傣族景颇族自治州', '怒江傈僳族自治州', '迪庆藏族自治州'],
    '西藏自治区': ['拉萨市', '日喀则市', '昌都市', '林芝市', '山南市', '那曲市', '阿里地区'],
    '陕西省': ['西安市', '铜川市', '宝鸡市', '咸阳市', '渭南市', '延安市', '汉中市', '榆林市', '安康市', '商洛市'],
    '甘肃省': ['兰州市', '嘉峪关市', '金昌市', '白银市', '天水市', '武威市', '张掖市', '平凉市', '酒泉市', '庆阳市', '定西市', '陇南市', '临夏回族自治州', '甘南藏族自治州'],
    '青海省': ['西宁市', '海东市', '海北藏族自治州', '黄南藏族自治州', '海南藏族自治州', '果洛藏族自治州', '玉树藏族自治州', '海西蒙古族藏族自治州'],
    '宁夏回族自治区': ['银川市', '石嘴山市', '吴忠市', '固原市', '中卫市'],
    '新疆维吾尔自治区': ['乌鲁木齐市', '克拉玛依市', '吐鲁番市', '哈密市', '昌吉回族自治州', '博尔塔拉蒙古自治州', '巴音郭楞蒙古自治州', '阿克苏地区', '克孜勒苏柯尔克孜自治州', '喀什地区', '和田地区', '伊犁哈萨克自治州', '塔城地区', '阿勒泰地区']
  }

  // 加载系统设置数据
  const loadSettings = async () => {
    try {
      setLoading(true)
      console.log('开始加载系统设置...')
      
      const [settingsData, operationAreasData] = await Promise.all([
        settingsService.getSettings(),
        settingsService.getOperationAreas()
      ])
      
      console.log('系统设置数据:', settingsData)
      console.log('运营范围数据:', operationAreasData)
      
      if (!settingsData) {
        throw new Error('系统设置数据为空')
      }
      
      setSettings(settingsData)
      setOperationAreas(operationAreasData || [])
      
      if (settingsData.basicSettings) {
        form.setFieldsValue(settingsData.basicSettings)
      }
    } catch (error: any) {
      console.error('加载系统设置失败:', error)
      const errorMessage = error?.message || error?.response?.data?.message || '加载系统设置失败'
      message.error(`加载系统设置失败: ${errorMessage}`)
      // 不设置settings为null，避免页面白屏，使用默认值
    } finally {
      setLoading(false)
    }
  }

  // 加载运营范围数据
  const loadOperationAreas = async () => {
    try {
      setOperationAreasLoading(true)
      const areas = await settingsService.getOperationAreas()
      setOperationAreas(areas)
    } catch (error) {
      console.error('加载运营范围失败:', error)
      message.error('加载运营范围失败')
    } finally {
      setOperationAreasLoading(false)
    }
  }

  // 添加运营区域
  const addOperationArea = async () => {
    if (!newArea.province || !newArea.city) {
      message.warning('请选择省份和城市')
      return
    }
    
    try {
      const newAreaData = await settingsService.addOperationArea(newArea.province, newArea.city)
      setOperationAreas([...operationAreas, newAreaData])
      setNewArea({ province: '', city: '' })
      message.success('添加成功')
    } catch (error: any) {
      message.error(error.message || '添加失败')
    }
  }

  // 删除运营区域
  const deleteOperationArea = async (id: string) => {
    try {
      await settingsService.deleteOperationArea(id)
      setOperationAreas(operationAreas.filter(area => area.id !== id))
      message.success('删除成功')
    } catch (error) {
      message.error('删除失败')
    }
  }

  const submitChangePassword = async () => {
    try {
      const v = await pwdForm.validateFields()
      await authService.changePassword({
        oldPassword: v.oldPassword,
        newPassword: v.newPassword,
      })
      message.success('密码修改成功')
      setPwdModalOpen(false)
      pwdForm.resetFields()
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'errorFields' in e) return
    }
  }

  // 保存基本设置
  const saveBasicSettings = async () => {
    try {
      const values = await form.validateFields()
      console.log('提交的表单数据:', values)
      
      const updatedSettings = await settingsService.updateSettings({
        basicSettings: values
      })
      
      console.log('更新后的设置:', updatedSettings)
      setSettings(updatedSettings)
      message.success('基本设置保存成功')
    } catch (error: any) {
      console.error('保存设置失败:', error)
      const errorMessage = error?.message || error?.response?.data?.message || '保存失败'
      message.error(`保存失败: ${errorMessage}`)
    }
  }

  // 清除缓存
  const clearCache = () => {
    Modal.confirm({
      title: '确认清除缓存',
      content: '清除缓存后，系统性能可能会暂时下降，确定要继续吗？',
      onOk: async () => {
        try {
          const result = await settingsService.maintenanceAction('clear-cache')
          setSettings(prev => prev ? { ...prev, maintenance: result } : null)
          message.success('缓存清除成功')
        } catch (error) {
          message.error('清除缓存失败')
        }
      }
    })
  }

  // 数据备份
  const backupData = () => {
    Modal.confirm({
      title: '确认数据备份',
      content: '开始备份系统数据，这可能需要几分钟时间。',
      onOk: async () => {
        try {
          const result = await settingsService.maintenanceAction('backup-data')
          setSettings(prev => prev ? { ...prev, maintenance: result } : null)
          message.success('数据备份成功')
        } catch (error) {
          message.error('数据备份失败')
        }
      }
    })
  }

  // 检查更新
  const checkUpdate = () => {
    message.info('正在检查更新...')
    setTimeout(() => {
      message.success('已是最新版本')
    }, 2000)
  }

  // 查看更新日志
  const viewUpdateLog = () => {
    Modal.info({
      title: '更新日志',
      content: (
        <div>
          <p><strong>v1.2.3 (2024-10-10)</strong></p>
          <ul>
            <li>优化用户界面体验</li>
            <li>修复已知问题</li>
            <li>提升系统性能</li>
          </ul>
          <p><strong>v1.2.2 (2024-09-15)</strong></p>
          <ul>
            <li>新增配送管理功能</li>
            <li>改进数据统计</li>
          </ul>
        </div>
      ),
      width: 600
    })
  }

  // Logo上传
  const logoUploadProps: UploadProps = {
    name: 'logo',
    listType: 'picture',
    showUploadList: false,
    beforeUpload: (file) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
      if (!isJpgOrPng) {
        message.error('只能上传 JPG/PNG 格式的图片!')
        return false
      }
      const isLt30M = file.size / 1024 / 1024 < 30
      if (!isLt30M) {
        message.error('图片大小不能超过 30MB!')
        return false
      }

      uploadImage(file, (formData) =>
        apiService.upload<{ url: string }>('/gardens/upload', formData),
        2
      ).then(({ url }) => {
        setSettings(prev => prev ? {
          ...prev,
          basicSettings: { ...prev.basicSettings, logoUrl: url }
        } : null)
        form.setFieldValue('logoUrl', url)
        message.success('Logo上传成功')
      }).catch(() => message.error('图片上传失败'))
      return false
    }
  }

  useEffect(() => {
    loadSettings()
  }, [])

  // 运营区域表格列定义
  const operationAreasColumns = [
    {
      title: '省份',
      dataIndex: 'province',
      key: 'province',
    },
    {
      title: '城市',
      dataIndex: 'city',
      key: 'city',
    },
    ...(isSuperAdmin
      ? [
          {
            title: '操作',
            key: 'action',
            render: (_: any, record: OperationArea) => (
              <Popconfirm
                title="确定删除这个区域吗？"
                onConfirm={() => deleteOperationArea(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ]

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>加载系统设置中...</div>
      </div>
    )
  }

  if (!settings) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="danger">加载系统设置失败</Text>
        <div style={{ marginTop: '16px' }}>
          <Button type="primary" onClick={() => loadSettings()}>
            重试
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* 页面标题 */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>系统设置</Title>
        <Text type="secondary">配置系统基本参数和高级功能</Text>
      </div>

      <Alert
        type={canManageAdmins ? 'info' : 'warning'}
        showIcon
        style={{ marginBottom: 24 }}
        message={
          canManageAdmins
            ? '给其他人开通后台子账号：请点下方按钮，或使用左侧菜单「子账号授权」'
            : '给其他人开通后台子账号：需要超级管理员（母账号）操作'
        }
        description={
          canManageAdmins ? (
            <Space wrap>
              <Text>在独立页面集中管理子账号，或在本页向下找到「管理员账号」卡片。</Text>
              <Button type="primary" icon={<UserAddOutlined />} onClick={() => navigate('/admin-accounts')}>
                打开子账号授权
              </Button>
            </Space>
          ) : (
            <Text>
              您当前不是超级管理员，无法在此添加或删除其他管理员。请使用超级管理员账号登录，进入左侧「子账号授权」，或联系本单位超级管理员为您开通子账号。
            </Text>
          )
        }
      />

      {/* 基本设置 */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <SettingOutlined style={{ fontSize: '18px', color: '#1890ff', marginRight: '8px' }} />
          <Title level={4} style={{ margin: 0 }}>基本设置</Title>
        </div>
        
        <Form
          form={form}
          layout="vertical"
          initialValues={settings.basicSettings}
          onFinish={saveBasicSettings}
        >
          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <Form.Item label="小程序名称" name="appName">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="应用版本" name="appVersion">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="公司名称" name="companyName">
                <Input placeholder="请输入公司名称" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="联系电话" name="contactPhone">
                <Input placeholder="请输入联系电话" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="客服邮箱" name="contactEmail">
                <Input placeholder="请输入客服邮箱" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Logo上传" name="logoUrl">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {settings?.basicSettings?.logoUrl && (
                    <Avatar size={64} src={settings.basicSettings.logoUrl} />
                  )}
                  {isSuperAdmin && (
                    <Upload {...logoUploadProps}>
                      <Button icon={<UploadOutlined />}>更换Logo</Button>
                    </Upload>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <Form.Item label="客服电话" name="servicePhone">
                <Input placeholder="例如：400-123-4567" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="客服工作时间" name="serviceHours">
                <Input placeholder="例如：周一至周五 9:00-18:00" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>
          </Row>

          {isSuperAdmin && (
            <div style={{ textAlign: 'right' }}>
              <Button type="primary" htmlType="submit">
                保存设置
              </Button>
            </div>
          )}
        </Form>
      </Card>

      {/* 运营范围管理 */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <GlobalOutlined style={{ fontSize: '18px', color: '#52c41a', marginRight: '8px' }} />
            <Title level={4} style={{ margin: 0 }}>运营范围管理</Title>
          </div>
          {isSuperAdmin && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowOperationAreasModal(true)}>
              添加区域
            </Button>
          )}
        </div>
        
        <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
          管理配送服务覆盖的省市区域，当前共 {operationAreas.length} 个区域
        </Text>
        
        <Table
          columns={operationAreasColumns}
          dataSource={operationAreas}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>

      {/* 通知设置 */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <BellOutlined style={{ fontSize: '18px', color: '#52c41a', marginRight: '8px' }} />
          <Title level={4} style={{ margin: 0 }}>通知设置</Title>
        </div>
        
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>新订单通知</Text>
              <Switch 
                checked={settings.notification.newOrder} 
                onChange={async (checked) => {
                  try {
                    const updatedSettings = await settingsService.updateSettings({
                      notification: { ...settings.notification, newOrder: checked }
                    })
                    setSettings(updatedSettings)
                    message.success('设置已更新')
                  } catch (error) {
                    message.error('更新失败')
                  }
                }}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>农户审核通知</Text>
              <Switch 
                checked={settings.notification.farmerVerify} 
                onChange={async (checked) => {
                  try {
                    const updatedSettings = await settingsService.updateSettings({
                      notification: { ...settings.notification, farmerVerify: checked }
                    })
                    setSettings(updatedSettings)
                    message.success('设置已更新')
                  } catch (error) {
                    message.error('更新失败')
                  }
                }}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>内容审核通知</Text>
              <Switch 
                checked={settings.notification.contentVerify} 
                onChange={async (checked) => {
                  try {
                    const updatedSettings = await settingsService.updateSettings({
                      notification: { ...settings.notification, contentVerify: checked }
                    })
                    setSettings(updatedSettings)
                    message.success('设置已更新')
                  } catch (error) {
                    message.error('更新失败')
                  }
                }}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>订单异常通知</Text>
              <Switch 
                checked={settings.notification.orderException} 
                onChange={async (checked) => {
                  try {
                    const updatedSettings = await settingsService.updateSettings({
                      notification: { ...settings.notification, orderException: checked }
                    })
                    setSettings(updatedSettings)
                    message.success('设置已更新')
                  } catch (error) {
                    message.error('更新失败')
                  }
                }}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* 安全设置 */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <SafetyOutlined style={{ fontSize: '18px', color: '#fa8c16', marginRight: '8px' }} />
          <Title level={4} style={{ margin: 0 }}>安全设置</Title>
        </div>
        
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>登录有效期</Text>
              <Select 
                value={settings.security.loginDuration} 
                onChange={async (value) => {
                  try {
                    const updatedSettings = await settingsService.updateSettings({
                      security: { ...settings.security, loginDuration: value }
                    })
                    setSettings(updatedSettings)
                    message.success('设置已更新')
                  } catch (error) {
                    message.error('更新失败')
                  }
                }}
                style={{ width: 120 }}
              >
                <Option value="1天">1天</Option>
                <Option value="7天">7天</Option>
                <Option value="30天">30天</Option>
                <Option value="永久">永久</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>双重身份验证</Text>
              <Switch 
                checked={settings.security.twoFactorAuth} 
                onChange={async (checked) => {
                  try {
                    const updatedSettings = await settingsService.updateSettings({
                      security: { ...settings.security, twoFactorAuth: checked }
                    })
                    setSettings(updatedSettings)
                    message.success('设置已更新')
                  } catch (error) {
                    message.error('更新失败')
                  }
                }}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>操作日志记录</Text>
              <Switch 
                checked={settings.security.operationLog} 
                onChange={async (checked) => {
                  try {
                    const updatedSettings = await settingsService.updateSettings({
                      security: { ...settings.security, operationLog: checked }
                    })
                    setSettings(updatedSettings)
                    message.success('设置已更新')
                  } catch (error) {
                    message.error('更新失败')
                  }
                }}
              />
            </div>
          </Col>
          <Col xs={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderTop: '1px solid #f0f0f0' }}>
              <div>
                <Text>登录密码</Text>
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                  使用当前登录账号的原密码即可修改（适用于 admin、superadmin 等任意管理员账号）
                </div>
              </div>
              <Button type="link" onClick={() => setPwdModalOpen(true)}>修改密码</Button>
            </div>
          </Col>
        </Row>
      </Card>

      {canManageAdmins && <AdminAccountsPanel />}

      {/* 系统维护 */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <ToolOutlined style={{ fontSize: '18px', color: '#722ed1', marginRight: '8px' }} />
          <Title level={4} style={{ margin: 0 }}>系统维护</Title>
        </div>
        
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <div>
                <Text>清除缓存</Text>
                <div style={{ fontSize: '12px', color: '#999' }}>当前缓存: {settings.maintenance.cacheSize}MB</div>
              </div>
              <Button onClick={clearCache}>清除</Button>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <div>
                <Text>数据备份</Text>
                <div style={{ fontSize: '12px', color: '#999' }}>上次备份: {settings.maintenance.lastBackupTime}</div>
              </div>
              <Button onClick={backupData}>备份</Button>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>维护模式</Text>
              <Switch 
                checked={settings.maintenance.maintenanceMode} 
                onChange={async (checked) => {
                  try {
                    const updatedSettings = await settingsService.updateSettings({
                      maintenance: { ...settings.maintenance, maintenanceMode: checked }
                    })
                    setSettings(updatedSettings)
                    message.success(checked ? '维护模式已开启' : '维护模式已关闭')
                  } catch (error) {
                    message.error('更新失败')
                  }
                }}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* 版本信息 */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <InfoCircleOutlined style={{ fontSize: '18px', color: '#13c2c2', marginRight: '8px' }} />
          <Title level={4} style={{ margin: 0 }}>版本信息</Title>
        </div>
        
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>当前版本</Text>
              <Tag color="blue">v{settings.version.current}</Tag>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>检查更新</Text>
              <Button type="link" onClick={checkUpdate}>
                {settings.version.hasUpdate ? '有新版本可用' : '已是最新版本'}
              </Button>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
              <Text>更新日志</Text>
              <Button type="link" onClick={viewUpdateLog}>查看</Button>
            </div>
          </Col>
        </Row>
      </Card>

      <Modal
        title="修改密码"
        open={pwdModalOpen}
        onOk={submitChangePassword}
        onCancel={() => {
          setPwdModalOpen(false)
          pwdForm.resetFields()
        }}
        okText="确定"
        destroyOnClose
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item
            label="原密码"
            name="oldPassword"
            rules={[{ required: true, message: '请输入原密码' }]}
          >
            <Input.Password placeholder="原密码" autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '新密码至少6位' },
            ]}
          >
            <Input.Password placeholder="至少6位" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            label="确认新密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'))
                },
              }),
            ]}
          >
            <Input.Password placeholder="再次输入新密码" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 运营范围管理弹窗 */}
      <Modal
        title="添加运营区域"
        open={showOperationAreasModal}
        onCancel={() => {
          setShowOperationAreasModal(false)
          setNewArea({ province: '', city: '' })
        }}
        onOk={addOperationArea}
        okText="添加"
        cancelText="取消"
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '12px' }}>
            <Text strong>选择省份</Text>
          </div>
          <Select
            placeholder="选择省份"
            value={newArea.province}
            onChange={(value) => setNewArea(prev => ({ ...prev, province: value, city: '' }))}
            style={{ width: '100%' }}
          >
            {provinces.map(province => (
              <Option key={province} value={province}>{province}</Option>
            ))}
          </Select>
        </div>
        <div>
          <div style={{ marginBottom: '12px' }}>
            <Text strong>选择城市</Text>
          </div>
          <Select
            placeholder="选择城市"
            value={newArea.city}
            onChange={(value) => setNewArea(prev => ({ ...prev, city: value }))}
            style={{ width: '100%' }}
            disabled={!newArea.province}
          >
            {newArea.province && cities[newArea.province]?.map(city => (
              <Option key={city} value={city}>{city}</Option>
            ))}
          </Select>
        </div>
      </Modal>
    </div>
  )
}

export default SystemSettings
